import { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { loginRequest, graphConfig, getDynamicMsalInstance } from 'config/authConfig';
import { UserProfile } from 'types/auth';

export interface MicrosoftGraphUser {
  id: string;
  displayName: string;
  mail: string;
  userPrincipalName: string;
  givenName: string;
  surname: string;
  jobTitle?: string;
  officeLocation?: string;
  businessPhones: string[];
  mobilePhone?: string;
}

/**
 * Initiates Microsoft OAuth login flow with dynamic configuration
 */
export const initiateMicrosoftLogin = async (clientId?: string, tenantId?: string): Promise<AuthenticationResult> => {
  try {
    const msalInstanceToUse = getDynamicMsalInstance(clientId, tenantId);
    // Always ask which account: after a sign-out, the button must not silently sign
    // the previous Microsoft account straight back in.
    const response = await msalInstanceToUse.loginPopup({ ...loginRequest, prompt: 'select_account' });
    return response;
  } catch (error) {
    console.error('Microsoft login failed:', error);
    throw error;
  }
};

/**
 * Gets access token for Microsoft Graph API
 */
export const getMicrosoftGraphToken = async (account: AccountInfo, msalInstance?: any): Promise<string> => {
  try {
    // Use provided instance or fall back to default
    const instance = msalInstance || getDynamicMsalInstance();
    
    const silentRequest = {
      ...loginRequest,
      account: account
    };

    const response = await instance.acquireTokenSilent(silentRequest);
    return response.accessToken;
  } catch (error) {
    console.error('Failed to get Graph token:', error);
    // Try interactive token acquisition if silent fails
    const instance = msalInstance || getDynamicMsalInstance();
    const response = await instance.acquireTokenPopup(loginRequest);
    return response.accessToken;
  }
};

/**
 * Fetches user profile from Microsoft Graph API
 */
export const fetchMicrosoftUserProfile = async (accessToken: string): Promise<MicrosoftGraphUser> => {
  try {
    const response = await fetch(graphConfig.graphMeEndpoint, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Graph API error: ${response.status} ${response.statusText}`);
    }

    const userProfile = await response.json();
    return userProfile;
  } catch (error) {
    console.error('Failed to fetch user profile from Graph API:', error);
    throw error;
  }
};

/**
 * Maps Microsoft Graph user to application UserProfile format
 */
export const mapMicrosoftUserToProfile = (
  microsoftUser: MicrosoftGraphUser,
  account: AccountInfo
): UserProfile => {
  return {
    uid: microsoftUser.id, // Use Microsoft ID as uid
    id: microsoftUser.id,
    avatar: undefined, // Microsoft Graph doesn't provide avatar URL in basic profile
    email: microsoftUser.mail || microsoftUser.userPrincipalName,
    name: microsoftUser.displayName || `${microsoftUser.givenName || ''} ${microsoftUser.surname || ''}`.trim(),
    role: 'user', // Default role, should be mapped from your backend
    tier: 'free' // Default tier, should be mapped from your backend
  };
};

/**
 * Complete Microsoft OAuth flow with profile fetching
 */
export const completeMicrosoftOAuthFlow = async (
  clientId?: string,
  tenantId?: string
): Promise<{
  userProfile: UserProfile;
  accessToken: string;
  account: AccountInfo;
}> => {
  try {
    // Step 1: Initiate login
    const authResult = await initiateMicrosoftLogin(clientId, tenantId);
    
    if (!authResult.account) {
      throw new Error('No account information received from Microsoft');
    }    // Step 2: Get Graph API token - use the same MSAL instance
    const msalInstance = getDynamicMsalInstance(clientId, tenantId);
    const graphToken = await getMicrosoftGraphToken(authResult.account, msalInstance);

    // Step 3: Fetch user profile
    const microsoftUser = await fetchMicrosoftUserProfile(graphToken);

    // Step 4: Map to application format
    const userProfile = mapMicrosoftUserToProfile(microsoftUser, authResult.account);

    return {
      userProfile,
      accessToken: authResult.accessToken,
      account: authResult.account
    };
  } catch (error) {
    console.error('Microsoft OAuth flow failed:', error);
    throw error;
  }
};

/**
 * Logout from Microsoft account
 */
export const logoutMicrosoft = async (clientId?: string, tenantId?: string): Promise<void> => {
  try {
    const msalInstance = getDynamicMsalInstance(clientId, tenantId);
    await msalInstance.logoutPopup({
      postLogoutRedirectUri: window.location.origin,
    });
  } catch (error) {
    console.error('Microsoft logout failed:', error);
    throw error;
  }
};

/**
 * Get current Microsoft account if user is logged in
 */
export const getCurrentMicrosoftAccount = (clientId?: string, tenantId?: string): AccountInfo | null => {
  const msalInstance = getDynamicMsalInstance(clientId, tenantId);
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
};

/**
 * Check if user is logged in with Microsoft
 */
export const isMicrosoftLoggedIn = (clientId?: string, tenantId?: string): boolean => {
  const msalInstance = getDynamicMsalInstance(clientId, tenantId);
  return msalInstance.getAllAccounts().length > 0;
};
